import Papa from "papaparse";

/**
 * A problem with one line of a CSV. The line number counts the header, so it matches the row
 * number a spreadsheet shows.
 */
export interface ICsvIssue {
  line: number;
  message: string;
}

/** One data line, keyed by its normalized header. */
export interface ICsvRow {
  line: number;
  values: Record<string, string>;
}

export interface ICsvFile {
  /** Normalized header names, in the order they appear. */
  headers: string[];
  rows: ICsvRow[];
  /** Set when the file cannot be read at all, in which case `rows` is empty. */
  fatal?: string;
}

/** What a reader produced from a CSV: the rows it accepted and the lines it could not use. */
export interface ICsvParseResult<T> {
  rows: T[];
  issues: ICsvIssue[];
  fatal?: string;
}

/**
 * Header spelling varies between exports, so they are matched on letters and digits alone. A
 * column written `Start Date`, `start_date` or `STARTDATE` all resolve to `startdate`.
 */
export const normalizeHeader = (header: string): string => header.toLowerCase().replace(/[^a-z0-9]/g, "");

const isBlankLine = (cells: string[]): boolean => cells.every((cell) => !cell?.trim());

/**
 * Reads a CSV into headered rows.
 *
 * Parsed without Papa's own header and empty-line handling so that every row keeps its true line
 * number for error reporting, and so that a line of bare delimiters - how spreadsheets pad the end
 * of an export - is dropped rather than read as a row of empty cells.
 */
export const parseCsvText = (text: string): ICsvFile => {
  const parsed = Papa.parse<string[]>(text, { skipEmptyLines: false });

  // A quoting fault puts every cell boundary after it in doubt, so the file is refused outright
  // rather than read in part. Row faults are reported per line by the readers instead.
  //
  // Papa's UndetectableDelimiter is deliberately not one of these: it is raised for an empty file
  // and for any single-column file, where defaulting to a comma is the right answer.
  const structural = parsed.errors.find((error) => error.code === "MissingQuotes" || error.code === "InvalidQuotes");
  if (structural) {
    const at = typeof structural.row === "number" ? ` at line ${structural.row + 1}` : "";
    return { headers: [], rows: [], fatal: `The file is not valid CSV${at}: ${structural.message}` };
  }

  const lines = parsed.data;
  const headerIndex = lines.findIndex((cells) => !isBlankLine(cells));
  if (headerIndex < 0) {
    return { headers: [], rows: [], fatal: "The file is empty." };
  }

  const headers = lines[headerIndex].map((cell) => normalizeHeader(cell ?? ""));
  const rows: ICsvRow[] = [];

  for (let index = headerIndex + 1; index < lines.length; index++) {
    const cells = lines[index];
    if (isBlankLine(cells)) continue;
    const values: Record<string, string> = {};
    headers.forEach((header, column) => {
      if (header) values[header] = (cells[column] ?? "").trim();
    });
    rows.push({ line: index + 1, values });
  }

  return { headers, rows };
};

/** Reads a chosen file, then parses it with {@link parseCsvText}. */
export const parseCsvFile = async (file: File): Promise<ICsvFile> => {
  try {
    return parseCsvText(await file.text());
  } catch {
    return { headers: [], rows: [], fatal: "The file could not be read." };
  }
};

// Only the fields that would otherwise break the row need quoting.
const escapeCsvField = (value: string): string => (/[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

/**
 * Writes rows as CSV text.
 *
 * The counterpart to {@link parseCsvText}: what this writes is what that reads, so an exported
 * file can go straight back into an import.
 */
export const toCsvText = (headers: string[], rows: string[][]): string =>
  `${[headers, ...rows].map((cells) => cells.map(escapeCsvField).join(",")).join("\n")}\n`;

/**
 * Names the columns a reader needs but the file does not have, as a message fit to show the admin.
 * Returns undefined when every required column is present.
 */
export const missingHeaders = (file: ICsvFile, required: string[]): string | undefined => {
  const absent = required.filter((header) => !file.headers.includes(header));
  if (!absent.length) return undefined;
  return `The file is missing the column${absent.length > 1 ? "s" : ""} ${absent.join(", ")}. Found: ${
    file.headers.filter(Boolean).join(", ") || "nothing"
  }.`;
};

const TRUE_VALUES = new Set(["true", "yes", "y", "1"]);
const FALSE_VALUES = new Set(["false", "no", "n", "0"]);

/** Reads a boolean cell. Returns undefined when the cell holds something it cannot interpret. */
export const parseCsvBoolean = (value: string | undefined): boolean | undefined => {
  const text = (value ?? "").toLowerCase();
  if (TRUE_VALUES.has(text)) return true;
  if (FALSE_VALUES.has(text)) return false;
  return undefined;
};

/** Reads a whole number of zero or more. Returns undefined for anything else, including decimals. */
export const parseCsvWholeNumber = (value: string | undefined): number | undefined => {
  const text = (value ?? "").trim();
  if (!/^\d+$/.test(text)) return undefined;
  return Number(text);
};
